import type { DialogProps } from '@mui/material/Dialog';
import type { PaperProps } from '@mui/material/Paper';
import type { Breakpoint } from '@mui/material/styles';
import type { ReactNode, Ref, SyntheticEvent } from 'react';

import type { StdDialogTitleProps } from 'components/Dialogs/StdDialogTitle';

export const DIALOG_CLOSE_EVENTS = ['BACKDROP_CLICK', 'CLOSE_BUTTON', 'ESCAPE_KEY_DOWN'] as const;

/** A close from the dialog chrome: the backdrop, the X or Escape. */
export type DialogCloseEvent = (typeof DIALOG_CLOSE_EVENTS)[number];

/** The reasons StdDialog reports on its own. */
export type StdDialogCloseReason = DialogCloseEvent | 'ENTER_KEY_DOWN';

export type StdDialogSize = Breakpoint | 'custom';

export type StdDialogMinHeight = 'xs' | 'sm' | 'md' | 'lg';

/**
 * `reason` is a StdDialogCloseReason, or the reason a caller passed to the ref handle's `handleClose`.
 */
export type StdDialogCloseHandler = (event: SyntheticEvent | null, reason: string) => void;

/** What `stdDialogRef` receives. */
export type StdDialogHandle = {
  /** Closes without calling `onClose`: resolves with `reason` and removes the dialog. */
  closeDialog: (reason?: string) => void;
  /** The id of the dialog's paper element. */
  getMainId: () => string;
  /** Closes the way the X does: calls `onClose` when set, otherwise `closeDialog`. */
  handleClose: (event?: SyntheticEvent | null, reason?: string) => void;
};

export type StdDialogProps = Omit<DialogProps, 'onClose' | 'open' | 'title'> & {
  /** Title bar background. Defaults to the paper color. */
  colorBar?: string;
  dialogId?: string;
  /** MUI dropped `disableBackdropClick`; StdDialog still honors it. */
  disableBackdropClick?: boolean;
  guideId?: string;
  /** Ignored for full-screen dialogs. */
  minHeightSize?: StdDialogMinHeight;
  onBack?: StdDialogTitleProps['onBack'];
  /** Without `onClose`, closing removes the dialog. */
  onClose?: StdDialogCloseHandler;
  /** Runs whenever the open state changes. */
  onOpen?: () => void;
  /** Leave unset to have the dialog track its own open state. */
  open?: boolean;
  /** Merged into StdDialog's paper props; `PaperProps` replaces them instead. */
  paperProps?: PaperProps;
  /** Called with the reason when the dialog closes itself. */
  resolve?: (reason?: string) => void;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  showDivider?: boolean;
  /** A breakpoint sets `maxWidth` and `fullWidth`; `custom` leaves them to the caller. */
  size?: StdDialogSize;
  // Some callers start their ref as undefined rather than null.
  stdDialogRef?: Ref<StdDialogHandle | undefined>;
  title?: ReactNode;
  TitleIcon?: ReactNode;
  titleProps?: Partial<StdDialogTitleProps>;
  /** The dialog type, reported in the Mixpanel `modal` event. */
  type?: string;
};
